import React, { useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import { FaTimes, FaMinus } from 'react-icons/fa';
import { theme, t } from '../theme';
import { toast } from 'react-toastify';
import { norm } from '../utils';
import { casarPorCodigo } from '../platform/scanner';
import BotaoScanner from './BotaoScanner';
import { AuthContext } from '../context/AuthContext';
import { moduloLigado, MODULO_ETIQUETAS } from '../config/modulos';
import EtiquetaModal from './EtiquetaModal';
import { loteParaEtiqueta } from './etiquetaValidade';

const StockExit = ({ produtoSelecionado = null, fecharModal = null, isModal = false,
                     etiquetaCodigo = null }) => {
    const { tenant } = useContext(AuthContext);
    const temEtiquetas = moduloLigado(tenant, MODULO_ETIQUETAS);
    const [lotesConsumidos, setLotesConsumidos] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [filteredProdutos, setFilteredProdutos] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [estoqueAtual, setEstoqueAtual] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [salvando, setSalvando] = useState(false);
    const modalRef = useRef(null);
    // Depois da baixa: "usei 4 pacotes, sobrou 1 aberto". E o momento em que a
    // etiqueta faz falta, e por isso ela mora aqui em vez de num botao proprio
    // — quem tira do estoque ja esta com a embalagem na mao.
    const [etiquetaDaSaida, setEtiquetaDaSaida] = useState(null);
    const [baixaFeita, setBaixaFeita] = useState(false);

    const [saida, setSaida] = useState({
        produto_id: '', nome_produto: '', codigo_barras: '',
        // Nasce em 1: a saida de uma unidade e o caso comum na cozinha, e
        // digitar "1" com a mao suja e atrito que da para tirar.
        quantidade: '1', motivo: 'Utilização na produção'
    });

    useEffect(() => { api.get('/produtos').then(r => setProdutos(r.data)).catch(() => {}); }, []);

    // O leitor da topbar manda o codigo por querystring em vez de abrir um
    // modal global: `/saida` ja e uma rota, entao nao ha estado novo para
    // manter em App.js. Espera os produtos carregarem — sem eles o
    // `handleCodigoEscaneado` nao teria em que casar o codigo — e roda uma vez
    // so, senao voltar para a tela reabriria a busca sozinha.
    const codigoDaUrl = new URLSearchParams(location.search).get('codigo');
    const codigoJaUsado = useRef(false);
    useEffect(() => {
        if (!codigoDaUrl || codigoJaUsado.current || !produtos.length) return;
        codigoJaUsado.current = true;
        handleCodigoEscaneado(codigoDaUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codigoDaUrl, produtos]);

    useEffect(() => {
        if (produtoSelecionado) {
            setSaida(prev => ({ ...prev, produto_id: produtoSelecionado.id, nome_produto: produtoSelecionado.nome, codigo_barras: produtoSelecionado.codigo_barras }));
            setEstoqueAtual(produtoSelecionado.estoque_atual || 0);
            setShowDropdown(false);
        }
    }, [produtoSelecionado]);

    useEffect(() => {
        // Enquanto o modal de etiqueta estiver aberto, este ouvinte fica MUDO.
        // Ele fecha a saida a qualquer mousedown fora de `modalRef`, e o modal
        // de etiqueta e IRMAO deste — entao todo clique la dentro contava como
        // "clicou fora": abrir um select fechava a tela inteira antes de a
        // lista sequer aparecer, e parecia que o formulario tinha salvado
        // sozinho (a baixa ja estava gravada, o que reforcava a impressao).
        if (!isModal || etiquetaDaSaida) return;
        const handler = (e) => { if (modalRef.current && !modalRef.current.contains(e.target)) fecharModal?.(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isModal, fecharModal, etiquetaDaSaida]);

    const handleNomeChange = (e) => {
        const v = e.target.value;
        setSaida(prev => ({ ...prev, nome_produto: v }));
        if (v.length > 0) { setFilteredProdutos(produtos.filter(p => norm(p.nome).includes(norm(v)))); setShowDropdown(true); }
        else { setShowDropdown(false); setEstoqueAtual(0); }
    };

    const handleCodigoBarrasChange = (e) => {
        const v = e.target.value;
        setSaida(prev => ({ ...prev, codigo_barras: v }));
        if (v.length > 0) { setFilteredProdutos(produtos.filter(p => p.codigo_barras?.includes(v) || p.codigo_barras_alternativo?.includes(v))); setShowDropdown(true); }
        else { setShowDropdown(false); setEstoqueAtual(0); }
    };

    // Codigo vindo da camera. Diferente da digitacao: aqui o codigo esta
    // COMPLETO, entao casamos por igualdade — `includes` selecionaria o produto
    // errado quando um EAN e prefixo de outro.
    const handleCodigoEscaneado = (codigo) => {
        setSaida(prev => ({ ...prev, codigo_barras: codigo }));
        const achados = casarPorCodigo(produtos, codigo);

        if (achados.length === 1) {
            handleProdutoSelect(achados[0]);
            return;
        }
        if (achados.length > 1) {
            setFilteredProdutos(achados);
            setShowDropdown(true);
            return;
        }
        setFilteredProdutos([]);
        setShowDropdown(false);
        // Nao basta avisar: leva pro cadastro com o codigo preenchido. Digitar
        // 13 digitos a mao e onde a usuaria desiste.
        toast.info(
            ({ closeToast }) => (
                <div>
                    <div style={{ marginBottom: 8 }}>
                        Código <strong>{codigo}</strong> não está cadastrado.
                    </div>
                    <button
                        type="button"
                        onClick={() => { closeToast(); navigate(`/produtos?codigo=${encodeURIComponent(codigo)}`); }}
                        style={{ ...t.btnPrimary, padding: '6px 12px', fontSize: 13 }}
                    >
                        Cadastrar este produto
                    </button>
                </div>
            ),
            { autoClose: 8000 }
        );
    };

    const handleProdutoSelect = (produto) => {
        setSaida(prev => ({ ...prev, produto_id: produto.id, nome_produto: produto.nome, codigo_barras: produto.codigo_barras }));
        setEstoqueAtual(produto.estoque_atual || 0); setShowDropdown(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!saida.produto_id) { setErrorMessage('Selecione um produto válido.'); return; }
        setSalvando(true);
        try {
            // `etiqueta_codigo` so vai quando a baixa veio de um QR. Nao muda o
            // fluxo (a saida continua FIFO por validade no backend) — deixa o
            // rastro de qual embalagem gerou a saida.
            const resposta = await api.post('/movimentacoes', { ...saida, tipo: 'saida',
                quantidade: parseInt(saida.quantidade),
                ...(etiquetaCodigo ? { etiqueta_codigo: etiquetaCodigo } : {}) });

            // Nem toda saida deixa embalagem aberta — tirar 2 caixas fechadas
            // para levar a outra cozinha nao gera etiqueta nenhuma. Por isso
            // aqui se PERGUNTA, em vez de abrir o modal por cima de todo mundo.
            if (temEtiquetas && !etiquetaCodigo) {
                // A saida acabou de apagar o lote se ele zerou — guardamos o que
                // ela consumiu para a etiqueta ainda ter numero de lote e
                // validade do fabricante. Ver `loteParaEtiqueta`.
                setLotesConsumidos(resposta?.data?.lotes_consumidos || []);
                setBaixaFeita(true);
                return;
            }
            if (fecharModal) fecharModal();
        } catch (error) {
            setErrorMessage(error.response?.data?.erro || 'Erro ao registrar saída.');
        } finally { setSalvando(false); }
    };

    /**
     * Pergunta se sobrou embalagem aberta e monta a etiqueta ja preenchida.
     *
     * A sugestao vem do backend (produto + lote mais antigo com saldo), entao a
     * validade do fabricante entra sozinha — e e ela que faz a etiqueta de
     * insumo valer a pena: a caixa diz dezembro, mas depois de aberta sao dias.
     */
    const oferecerEtiqueta = async () => {
        try {
            const r = await api.get('/api/etiquetas/sugestao', { params: { produto_id: saida.produto_id } });
            setEtiquetaDaSaida({
                origem: 'produto',
                produto_id: r.data.produto_id,
                lote_id: r.data.lote_id,
                nome: r.data.nome,
                conservacao: r.data.conservacao,
                quantidade: r.data.quantidade != null ? String(r.data.quantidade) : '',
                unidade_medida: r.data.unidade_medida || 'g',
                ...loteParaEtiqueta(r.data, lotesConsumidos),
                lotes: r.data.lotes || [],
                modo_validade: r.data.modo_validade || 'dias',
                prazo_dias: String(r.data.prazo_dias),
            });
        } catch {
            // Sem sugestao a etiqueta ainda sai; so entra menos preenchida.
            setEtiquetaDaSaida({ origem: 'produto', produto_id: saida.produto_id,
                nome: saida.nome_produto, conservacao: 'refrigerado' });
        }
    };

    const qtd = parseInt(saida.quantidade) || 0;
    const estoqueFinal = Math.max(estoqueAtual - qtd, 0);

    /**
     * O que aparece no lugar do formulario depois que a baixa foi registrada.
     *
     * A baixa JA ESTA GRAVADA aqui: fechar sem etiquetar nao perde nada, e e
     * por isso que "Nao precisa" e um botao normal e nao um cancelamento.
     */
    // Nunca desce abaixo de 1: saida de zero nao existe, e chegar a zero pelo
    // botao so criaria um estado invalido para a pessoa desfazer.
    const passo = (delta) => setSaida(prev => {
        const atual = parseInt(prev.quantidade) || 0;
        return { ...prev, quantidade: String(Math.max(1, atual + delta)) };
    });

    const estiloPasso = {
        flexShrink: 0, width: 48,
        border: `1px solid ${theme.colors.border}`, borderRadius: 10,
        background: '#fff', color: theme.colors.text,
        fontSize: 22, lineHeight: 1, cursor: 'pointer',
    };

    const renderDepoisDaBaixa = () => (
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
                Saída registrada
            </div>
            <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginBottom: 16 }}>
                Sobrou embalagem aberta para guardar?
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
                <button type="button" onClick={oferecerEtiqueta}
                    style={{ ...t.btnPrimary, justifyContent: 'center', padding: '12px 0' }}>
                    Imprimir etiqueta
                </button>
                <button type="button"
                    onClick={() => { setBaixaFeita(false); if (fecharModal) fecharModal(); else navigate('/produtos'); }}
                    style={{ ...t.btnSecondary, justifyContent: 'center' }}>
                    Não precisa
                </button>
            </div>
        </div>
    );

    const renderFormulario = () => (
        <>
            {errorMessage && <div style={t.error}>{errorMessage}</div>}
            <form onSubmit={handleSubmit}>
                {!produtoSelecionado && (
                    <>
                        <div style={{ marginBottom: 14 }}>
                            <label style={t.label}>Produto</label>
                            <div style={{ position: 'relative' }}>
                                <input value={saida.nome_produto} onChange={handleNomeChange} placeholder="Buscar por nome..." style={t.input} autoComplete="off" required />
                                {showDropdown && filteredProdutos.length > 0 && (
                                    <ul style={{ position: 'absolute', width: '100%', background: '#fff', border: `1px solid ${theme.colors.border}`, borderRadius: 10, boxShadow: theme.shadow.md, marginTop: 4, zIndex: 20, maxHeight: 160, overflow: 'auto', listStyle: 'none', padding: 0 }}>
                                        {filteredProdutos.map(p => (
                                            <li key={p.id} onClick={() => handleProdutoSelect(p)} style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 14, borderBottom: `1px solid ${theme.colors.borderLight}` }}
                                                onMouseEnter={ev => ev.currentTarget.style.background = theme.colors.bgSection} onMouseLeave={ev => ev.currentTarget.style.background = '#fff'}>
                                                <div style={{ fontWeight: 500 }}>{p.nome}</div>
                                                <div style={{ fontSize: 12, color: theme.colors.textMuted }}>{p.codigo_barras}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={t.label}>Código de Barras</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input value={saida.codigo_barras} onChange={handleCodigoBarrasChange} placeholder="Código..." style={{ ...t.input, flex: 1 }} autoComplete="off" />
                                <BotaoScanner onLido={handleCodigoEscaneado} />
                            </div>
                        </div>
                    </>
                )}

                {/* Estoque atual → final */}
                <div style={{ ...t.section, marginBottom: 14, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: theme.colors.textSecondary }}>Estoque atual: <strong style={{ color: theme.colors.text }}>{estoqueAtual}</strong></span>
                    <span style={{ color: qtd > estoqueAtual ? theme.colors.danger : theme.colors.success }}>
                        Após saída: <strong>{estoqueFinal}</strong>
                    </span>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={t.label}>Quantidade</label>
                    {/* Passo em vez de teclado: na cozinha a mao esta ocupada e o
                        teclado numerico cobre metade da tela. O campo continua
                        digitavel para quando a saida for de 40 unidades. */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <button type="button" onClick={() => passo(-1)} aria-label="Diminuir"
                            disabled={(parseInt(saida.quantidade) || 0) <= 1} style={estiloPasso}>−</button>
                        <input value={saida.quantidade} inputMode="numeric"
                            onChange={e => setSaida(prev => ({ ...prev, quantidade: e.target.value.replace(/\D/g, '') }))}
                            placeholder="0" style={{ ...t.input, textAlign: 'center', fontSize: 18, fontWeight: 700 }} required />
                        <button type="button" onClick={() => passo(1)} aria-label="Aumentar"
                            style={estiloPasso}>+</button>
                    </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={t.label}>Motivo da Saída</label>
                    <select value={saida.motivo} onChange={e => setSaida(prev => ({ ...prev, motivo: e.target.value }))} style={t.select} required>
                        <option value="">Selecione o motivo</option>
                        <option value="Utilização na produção">Utilização na produção</option>
                        <option value="Inventário">Inventário</option>
                        <option value="Venceu / Estragou">Venceu / Estragou</option>
                        <option value="Desperdício na produção">Desperdício na produção</option>
                        <option value="Testes culinários">Testes culinários</option>
                        <option value="Doação">Doação</option>
                        <option value="Perda não identificada">Perda não identificada</option>
                    </select>
                </div>

                <button type="submit" disabled={salvando} style={{ ...t.btnPrimary, width: '100%', justifyContent: 'center', marginTop: 8, background: theme.colors.danger, opacity: salvando ? 0.6 : 1 }}>
                    {salvando ? '⏳ Registrando...' : 'Registrar Saída'}
                </button>
            </form>
        </>
    );

    // Aparece por cima do que estiver na tela: a baixa ja foi registrada, e
    // esta e a pergunta seguinte ("sobrou embalagem aberta?").
    const modalDaEtiqueta = etiquetaDaSaida ? (
        <EtiquetaModal
            inicial={etiquetaDaSaida}
            onFechar={() => { setEtiquetaDaSaida(null); if (fecharModal) fecharModal(); }}
        />
    ) : null;

    if (isModal) {
        return (
        <>
            <div style={t.overlay}>
                <div ref={modalRef} style={{ ...t.modal, maxWidth: 480 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: theme.colors.dangerLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaMinus size={14} color={theme.colors.danger} />
                            </div>
                            <div>
                                <h3 style={{ ...t.h3, margin: 0 }}>Saída de Estoque</h3>
                                {produtoSelecionado && <p style={{ ...t.subtitle, margin: '2px 0 0' }}>{produtoSelecionado.nome}</p>}
                            </div>
                        </div>
                        <button onClick={fecharModal} style={t.btnIcon}><FaTimes size={16} color={theme.colors.textMuted} /></button>
                    </div>
                    {baixaFeita ? renderDepoisDaBaixa() : renderFormulario()}
                </div>
            </div>
        {modalDaEtiqueta}
        </>
        );
    }

    return (
        <>
        <div style={{ maxWidth: 520, margin: '16px auto', ...t.card, padding: '24px 32px' }}>
            <h2 style={{ ...t.h1, textAlign: 'center', marginBottom: 20 }}>Saída de Estoque</h2>
            {baixaFeita ? renderDepoisDaBaixa() : renderFormulario()}
        </div>
        {modalDaEtiqueta}
        </>
    );
};

export default StockExit;
